import { getUser } from "@synthesis/shared-auth";
import type { SelectItem, ServerSearch } from "@synthesis/shared-ui";
import { useQuery } from "@tanstack/react-query";

// ------------------------------ TYPES ------------------------------

export type GroupSearchParams = {
  search?: string;
  first?: number;
  max?: number;
};

export type GroupSearchResult = {
  id: string;
  name: string;
};

export type GroupSearchResultMapper = (group: GroupSearchResult) => SelectItem;

// ------------------------------ CONSTANTS ------------------------------

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_SERVER_URI;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;

const SEARCH_PATH = `/auth/realms/${KEYCLOAK_REALM}/custom-realm-resource-factory-admin-groups-search/apart-search`;

const MIN_SEARCH_LENGTH = 2;
export const GROUP_SEARCH_DEFAULT_PAGE_SIZE = 20;

const getSearchUrl = (): string => {
  if (import.meta.env.DEV) {
    return SEARCH_PATH;
  }

  return `${KEYCLOAK_URL}${SEARCH_PATH}`;
};

// ------------------------------ FETCH ------------------------------

const searchGroups = async (params: GroupSearchParams, signal?: AbortSignal): Promise<GroupSearchResult[]> => {
  const token = getUser()?.access_token;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const url = new URL(getSearchUrl(), import.meta.env.DEV ? window.location.origin : undefined);

  if (params.search) {
    url.searchParams.set("search", params.search);
  }

  if (params.first !== undefined) {
    url.searchParams.set("first", String(params.first));
  }

  if (params.max !== undefined) {
    url.searchParams.set("max", String(params.max));
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Keycloak groups search failed: ${response.status}`);
  }

  return response.json();
};

// ------------------------------ TANSTACK QUERY ------------------------------

export const groupSearchQueryKey = (params: GroupSearchParams) => ["group-search", params] as const;

export const useGroupSearch = <TData = GroupSearchResult[]>(params: GroupSearchParams, options?: { enabled?: boolean }) =>
  useQuery<GroupSearchResult[], Error, TData>({
    queryKey: groupSearchQueryKey(params),
    queryFn: async ({ signal }) => searchGroups(params, signal),
    enabled: options?.enabled ?? (params.search?.length ?? 0) >= MIN_SEARCH_LENGTH,
  });

// ------------------------------ SERVER SEARCH ------------------------------

const defaultGroupToSelectItem = (group: GroupSearchResult): SelectItem => ({
  value: group.id,
  label: group.name,
  data: group,
});

export const createGroupServerSearch = (
  pageSize = GROUP_SEARCH_DEFAULT_PAGE_SIZE,
  mapper: GroupSearchResultMapper = defaultGroupToSelectItem,
): ServerSearch => ({
  fetchItems: async (search, page, signal) => {
    const trimmedQuery = search?.trim();

    if (trimmedQuery && trimmedQuery.length < MIN_SEARCH_LENGTH) {
      return [];
    }

    const results = await searchGroups({ search: trimmedQuery || undefined, first: (page - 1) * pageSize, max: pageSize }, signal);

    return results.map(mapper);
  },
  getNextPageParam: (lastPage, allPages) => {
    if (!Array.isArray(lastPage) || lastPage.length < pageSize) {
      return undefined;
    }

    return allPages.length + 1;
  },
});
